export interface DroppedFsEntry {
  isFile: boolean
  isDirectory: boolean
  name: string
  file?: (success: (file: File) => void, error?: (err: DOMException) => void) => void
  createReader?: () => {
    readEntries: (
      success: (entries: DroppedFsEntry[]) => void,
      error?: (err: DOMException) => void
    ) => void
  }
}

export interface DataTransferItemLike {
  kind: string
  webkitGetAsEntry?: () => DroppedFsEntry | FileSystemEntry | null
}

export interface DataTransferLike {
  files?: FileList | File[] | null
  items?: ArrayLike<DataTransferItemLike> | null
}

type DirectoryReader = NonNullable<DroppedFsEntry['createReader']> extends () => infer R
  ? R
  : never

export function stampWebkitRelativePath(file: File, relativePath: string): File {
  try {
    Object.defineProperty(file, 'webkitRelativePath', {
      value: relativePath,
      configurable: true
    })
    return file
  } catch {
    const copy = new File([file], file.name, {
      type: file.type,
      lastModified: file.lastModified
    })
    Object.defineProperty(copy, 'webkitRelativePath', {
      value: relativePath,
      configurable: true
    })
    return copy
  }
}

function asDroppedEntry(entry: DroppedFsEntry | FileSystemEntry): DroppedFsEntry {
  return entry as DroppedFsEntry
}

function listFiles(files: FileList | File[] | null | undefined): File[] {
  if (!files) return []
  return Array.from(files)
}

/**
 * Native FileSystemEntry methods throw `TypeError: Illegal invocation` unless
 * called with the entry/reader as `this`. Never extract them as unbound functions.
 */
function getAsEntry(item: DataTransferItemLike): DroppedFsEntry | null {
  const getEntry = item.webkitGetAsEntry
  if (typeof getEntry !== 'function') return null
  try {
    const entry = getEntry.call(item)
    return entry ? asDroppedEntry(entry) : null
  } catch {
    return null
  }
}

function createDirectoryReader(dir: DroppedFsEntry): DirectoryReader | null {
  const createReader = dir.createReader
  if (typeof createReader !== 'function') return null
  return createReader.call(dir)
}

function readEntriesOnce(reader: DirectoryReader): Promise<DroppedFsEntry[]> {
  return new Promise((resolve, reject) => {
    const readEntries = reader.readEntries
    if (typeof readEntries !== 'function') {
      resolve([])
      return
    }
    readEntries.call(
      reader,
      (entries) => resolve(Array.from(entries ?? [])),
      reject
    )
  })
}

function readFileFromEntry(entry: DroppedFsEntry): Promise<File> {
  return new Promise((resolve, reject) => {
    const readFile = entry.file
    if (typeof readFile !== 'function') {
      reject(new TypeError('Not a file entry'))
      return
    }
    readFile.call(entry, resolve, reject)
  })
}

async function readAllDirectoryEntries(dir: DroppedFsEntry): Promise<DroppedFsEntry[]> {
  const reader = createDirectoryReader(dir)
  if (!reader) return []
  const all: DroppedFsEntry[] = []
  for (;;) {
    const batch = await readEntriesOnce(reader)
    if (batch.length === 0) break
    all.push(...batch)
  }
  return all
}

async function collectFromEntry(
  entry: DroppedFsEntry,
  parentPath: string,
  out: File[]
): Promise<void> {
  if (entry.isFile) {
    const file = await readFileFromEntry(entry)
    const relativePath = parentPath ? `${parentPath}/${file.name}` : file.name
    out.push(stampWebkitRelativePath(file, relativePath))
    return
  }
  if (entry.isDirectory) {
    const path = parentPath ? `${parentPath}/${entry.name}` : entry.name
    const children = await readAllDirectoryEntries(entry)
    for (const child of children) {
      await collectFromEntry(child, path, out)
    }
  }
}

function snapshotEntries(dataTransfer: DataTransferLike): DroppedFsEntry[] {
  const items = Array.from(dataTransfer.items ?? [])
  const entries: DroppedFsEntry[] = []
  for (const item of items) {
    if (item.kind !== 'file') continue
    const entry = getAsEntry(item)
    if (entry) entries.push(entry)
  }
  return entries
}

/**
 * Read a drag-and-drop. Windows folder drops often put inner files in
 * `dataTransfer.files` with an empty `webkitRelativePath`; directory entries
 * on `items` still have the folder name.
 *
 * `webkitGetAsEntry` must run synchronously in the drop handler before any
 * await — callers should invoke this immediately on drop.
 */
export async function readDroppedFiles(dataTransfer: DataTransferLike): Promise<File[]> {
  const fallback = listFiles(dataTransfer.files)
  const entries = snapshotEntries(dataTransfer)

  if (!entries.some((entry) => entry.isDirectory)) {
    return fallback
  }

  try {
    const collected: File[] = []
    for (const entry of entries) {
      await collectFromEntry(entry, '', collected)
    }
    return collected.length > 0 ? collected : fallback
  } catch {
    return fallback
  }
}
